'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalculatorIcon,
  CalendarIcon,
  EyeIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  PackageIcon,
  RefreshCcwIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  WaypointsIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { JobStatusEnum, SortEnum } from '@/enums'
import {
  useBulkDeleteProductPictureImports,
  useDeleteProductPictureImport,
  useProductPictureImports,
  useRetryProductPictureImport
} from '@/hooks'
import { useProductPictureImportsStore } from '@/store'
import { ProductPictureImportsFilterParams, ProductPictureImportType } from '@/types/product-picture-import'
import {
  ConfirmationDialog,
  DeleteConfirmationDialog,
  JobStatusBadge,
  LoadingState,
  ProductPictureImportsFilters,
  SortableHeader
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import { formatDateTime, formatNumber, tOptional } from '@/lib/utils'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductPictureImportId, setDeleteProductPictureImportId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [retryingProductPictureImportId, setRetryingProductPictureImportId] = useState<string | null>(null)
  const [retryConfirmProductPictureImportId, setRetryConfirmProductPictureImportId] = useState<string | null>(null)

  const { mutate: deleteProductPictureImportMutate, isPending: isDeleting } = useDeleteProductPictureImport()
  const { mutate: bulkDeleteProductPictureImportsMutate, isPending: isBulkDeleting } =
    useBulkDeleteProductPictureImports()
  const { mutate: retryProductPictureImportMutate } = useRetryProductPictureImport()

  const selectedProductPictureImportIds = useProductPictureImportsStore(state => state.selectedProductPictureImportIds)
  const toggleProductPictureImportSelection = useProductPictureImportsStore(
    state => state.toggleProductPictureImportSelection
  )
  const selectAllProductPictureImports = useProductPictureImportsStore(state => state.selectAllProductPictureImports)
  const deselectAllProductPictureImports = useProductPictureImportsStore(
    state => state.deselectAllProductPictureImports
  )

  const filters = useMemo<ProductPictureImportsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    // Parse statuses from URL (comma-separated string to array)
    const statusesParam = params.get('statuses')
    const statuses = statusesParam ? statusesParam.split(',').filter(Boolean) : undefined

    // Parse categories from URL (comma-separated string to array)
    const categoriesParam = params.get('categories')
    const categories = categoriesParam ? categoriesParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      statuses,
      categories,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: productPictureImportsData, isLoading, error, refetch, isFetching } = useProductPictureImports(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ProductPictureImportsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle array values (like statuses)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductPictureImportsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/import/picture')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (productPictureImportId: string) => {
    router.push(`/products/import/picture/${productPictureImportId}`)
  }

  const handleRetry = (productPictureImportId: string) => {
    setRetryConfirmProductPictureImportId(productPictureImportId)
  }

  const confirmRetry = () => {
    if (retryConfirmProductPictureImportId) {
      setRetryingProductPictureImportId(retryConfirmProductPictureImportId)
      retryProductPictureImportMutate(retryConfirmProductPictureImportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
          // Reset to page 1 when retrying
          handleFiltersChange({ page: 1 })
          toast.success(t('retry_started'))
          setRetryingProductPictureImportId(null)
          setRetryConfirmProductPictureImportId(null)
        },
        onError: (error: any) => {
          console.error('Error retrying productPictureImport:', error)
          toast.error(error.response?.data.message ?? t('error_retrying'))
          setRetryingProductPictureImportId(null)
          setRetryConfirmProductPictureImportId(null)
        }
      })
    }
  }

  const handleDelete = (productPictureImportId: string) => {
    setDeleteProductPictureImportId(productPictureImportId)
  }

  const renderActionItems = (productPictureImport: ProductPictureImportType, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(productPictureImport.id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('picture_import_details')}
        </ItemComponent>
        {productPictureImport.status === JobStatusEnum.FAILED && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleRetry(productPictureImport.id)
            }}
            disabled={retryingProductPictureImportId === productPictureImport.id}>
            <RefreshCcwIcon className="h-4 w-4" />
            {t('retry')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(productPictureImport.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteProductPictureImportId) {
      deleteProductPictureImportMutate(deleteProductPictureImportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductPictureImportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting model:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteProductPictureImportId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedProductPictureImportIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteProductPictureImports = () => {
    if (selectedProductPictureImportIds.length > 0) {
      bulkDeleteProductPictureImportsMutate(selectedProductPictureImportIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (productPictureImportsData && productPictureImportsData.items.length > 0) {
      if (selectedProductPictureImportIds.length === productPictureImportsData.items.length) {
        // If all productPictureImports are selected, deselect all
        deselectAllProductPictureImports()
      } else {
        // Otherwise, select all productPictureImports
        selectAllProductPictureImports(productPictureImportsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="h-5 w-5" />
                {t('bulk_picture_import')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" asChild>
                <Link href={`/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}>
                  <PackageIcon />
                  <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
                </Link>
              </Button>
              <Button asChild>
                <Link href="/products/import/picture/create">
                  <SquarePlusIcon />
                  <span className="hidden items-center gap-2 lg:flex">{t('create_picture_import')}</span>
                </Link>
              </Button>
              {selectedProductPictureImportIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductPictureImportIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductPictureImportsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {productPictureImportsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                productPictureImportsData &&
                                productPictureImportsData.items.length > 0 &&
                                selectedProductPictureImportIds.length === productPictureImportsData.items.length
                              }
                              onCheckedChange={() => {
                                handleSelectAll()
                              }}
                              onClick={e => {
                                e.stopPropagation()
                              }}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="successfulFiles"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('created_items')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="failedFiles"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('failed_items')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="processedFiles"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('total')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <WaypointsIcon className="h-4 w-4" />
                            {t('status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productPictureImportsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productPictureImportsData.items.map((productPictureImport, index) => (
                          <ContextMenu key={productPictureImport.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(productPictureImport.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedProductPictureImportIds.includes(productPictureImport.id)}
                                      onCheckedChange={() =>
                                        toggleProductPictureImportSelection(productPictureImport.id)
                                      }
                                      onClick={e => {
                                        e.stopPropagation()
                                      }}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="text-muted-foreground text-xs">{productPictureImport.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productPictureImport.successfulFiles, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productPictureImport.failedFiles, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productPictureImport.processedFiles, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div>
                                        <JobStatusBadge status={productPictureImport.status} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {productPictureImport.message ? tOptional(t, productPictureImport.message) : ''}
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productPictureImport.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productPictureImport.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(productPictureImport, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(productPictureImport, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productPictureImportsData.page}
                  totalPages={productPictureImportsData.pages}
                  totalItems={productPictureImportsData.total}
                  pageSize={productPictureImportsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteProductPictureImportId !== null}
        onCancel={() => setDeleteProductPictureImportId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteProductPictureImports}
        isDeleting={isBulkDeleting}
      />

      <ConfirmationDialog
        open={retryConfirmProductPictureImportId !== null}
        onCancel={() => setRetryConfirmProductPictureImportId(null)}
        onConfirm={confirmRetry}
        isLoading={retryingProductPictureImportId !== null}
        description={t('retry_confirmation_message')}
        confirmText={t('yes')}
        cancelText={t('no')}
        loadingText={t('retrying')}
      />
    </>
  )
}

export default List
